import type { PrivateFundingOpportunity } from "./types";

const LIST_PREFIX = "roott_private_funding_v1";
const ACTIVE_PREFIX = "roott_private_funding_active_v1";

const nowIso = () => new Date().toISOString();

function scopedListKey(userKey: string) {
  return `${LIST_PREFIX}:${userKey}`;
}

function scopedActiveKey(userKey: string) {
  return `${ACTIVE_PREFIX}:${userKey}`;
}

export function readPrivateFundingList(userKey: string): PrivateFundingOpportunity[] {
  try {
    const raw = localStorage.getItem(scopedListKey(userKey));
    return raw ? (JSON.parse(raw) as PrivateFundingOpportunity[]) : [];
  } catch {
    return [];
  }
}

export function writePrivateFundingList(
  userKey: string,
  list: PrivateFundingOpportunity[]
) {
  localStorage.setItem(scopedListKey(userKey), JSON.stringify(list));
}

export function upsertPrivateFundingOpportunity(
  userKey: string,
  item: Omit<PrivateFundingOpportunity, "id" | "createdAt" | "updatedAt"> & {
    id?: string;
    createdAt?: string;
  }
) {
  const list = readPrivateFundingList(userKey);
  const nextItem: PrivateFundingOpportunity = {
    ...item,
    id: item.id ?? `pf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: item.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  };

  const nextList = list.some((entry) => entry.id === nextItem.id)
    ? list.map((entry) => (entry.id === nextItem.id ? nextItem : entry))
    : [nextItem, ...list];

  writePrivateFundingList(userKey, nextList);
  return nextItem;
}

export function setActivePrivateFunding(userKey: string, id: string | null) {
  if (id == null) {
    localStorage.removeItem(scopedActiveKey(userKey));
    return;
  }
  localStorage.setItem(scopedActiveKey(userKey), id);
}

export function getActivePrivateFunding(userKey: string) {
  const activeId = localStorage.getItem(scopedActiveKey(userKey));
  if (!activeId) return null;
  return readPrivateFundingList(userKey).find((item) => item.id === activeId) ?? null;
}

export async function extractPrivateFundingFromUrl(url: string) {
  await new Promise((resolve) => window.setTimeout(resolve, 700));
  const parsed = new URL(url);
  const domainLabel = parsed.hostname
    .replace(/^www\./, "")
    .split(".")[0]
    .replace(/[-_]/g, " ");

  const normalizedFunderName = domainLabel
    .split(" ")
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(" ");

  return {
    funderName: normalizedFunderName || "Imported funding opportunity",
    amount: "",
    deadline: "",
    tags: [parsed.hostname.replace(/^www\./, ""), "Imported from URL"],
    summary: `Imported from ${parsed.hostname}${parsed.pathname !== "/" ? parsed.pathname : ""}`,
    whatTheyAreLookingFor:
      "Describe the fit, priorities, eligibility, and any special criteria from this opportunity.",
  };
}
